import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useGames } from '../context/GameContext';
import { safeNavigate } from '../utils/navigationUtils';
// import gameStateManager from '../services/GameStateManager';
import './ProfilePage.css';

const ProfilePage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { 
    games, 
    loading, 
    error, 
    fetchGames, 
    deleteGame,
    checkUnsavedChanges
  } = useGames();
  
  // const [selectedGame, setSelectedGame] = useState(null);
  const [expandedImages, setExpandedImages] = useState(new Set());
  const [statusMessage, setStatusMessage] = useState('');
  const [uploadingImages, setUploadingImages] = useState(new Set());
  const [deletingGames, setDeletingGames] = useState(new Set());
  const [editingDescriptions, setEditingDescriptions] = useState(new Set());
  const [showAllDescriptions, setShowAllDescriptions] = useState(false);
  // Admin-only: history debug panel toggle
  const [historyDebugVisible, setHistoryDebugVisible] = useState(() => {
    try {
      return localStorage.getItem('dnd_show_history_debug_panel') === 'true';
    } catch (_) {
      return false;
    }
  });
  // Add confirmation dialog state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [gameToDelete, setGameToDelete] = useState(null);

  // חישוב דינאמי של גובה ההדר והפוטר
  useEffect(() => {
    const calculateHeaderFooterHeight = () => {
      const header = document.querySelector('.header');
      const footer = document.querySelector('.footer');
      
      if (header && footer) {
        const headerHeight = header.offsetHeight;
        const footerHeight = footer.offsetHeight;
        const totalHeight = headerHeight + footerHeight;
        
        // הגדרת CSS custom property
        document.documentElement.style.setProperty('--header-footer-height', `${totalHeight}px`);
      }
    };

    // חישוב ראשוני
    calculateHeaderFooterHeight();
    
    // חישוב מחדש אחרי שההדר והפוטר נטענים לחלוטין
    const timeoutId = setTimeout(calculateHeaderFooterHeight, 100);
    
    // חישוב מחדש במקרה של שינוי גודל חלון
    const handleResize = () => {
      calculateHeaderFooterHeight();
    };
    
    window.addEventListener('resize', handleResize);
    
    // ניקוי
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    fetchGames();
  }, [fetchGames]); // Now safe to include fetchGames since it's memoized with useCallback

  useEffect(() => {
    if (currentUser) {
      fetchGames();
    }
  }, [currentUser, fetchGames]);

  const handleLoadGame = (gameId) => {
    checkUnsavedChanges(() => {
      safeNavigate(navigate, `/app?gameId=${gameId}`);
    });
  };

  const handleDeleteGame = async (gameId) => {
    // Find the game to show its name in the dialog
    const game = games.find(g => g.game_state_id === gameId);
    setGameToDelete({ gameId, gameName: game?.name || 'Unknown Game' });
    setShowDeleteConfirm(true);
  };

  const handleConfirmDeleteGame = async () => {
    if (!gameToDelete) return;

    // Start loading state
    setDeletingGames(prev => new Set([...prev, gameToDelete.gameId]));
    
    try {
      const success = await deleteGame(gameToDelete.gameId);
      if (success) {
        setStatusMessage('Game deleted successfully!');
        setTimeout(() => setStatusMessage(''), 3000);
      } else {
        setStatusMessage('Failed to delete game. Please try again.');
        setTimeout(() => setStatusMessage(''), 5000);
      }
    } finally {
      // End loading state
      setDeletingGames(prev => {
        const newSet = new Set(prev);
        newSet.delete(gameToDelete.gameId);
        return newSet;
      });
      // Close dialog
      setShowDeleteConfirm(false);
      setGameToDelete(null);
    }
  };

  const handleCancelDeleteGame = () => {
    setShowDeleteConfirm(false);
    setGameToDelete(null);
  };

  // const handleShareGame = async (game, e) => {
  //   e.stopPropagation();
  //   const description = prompt('Add a description for this shared game:', '');
  //   
  //   // Only proceed if user didn't cancel and description is valid
  //   if (description !== null) {
  //     // Trim the description (allow empty descriptions, but not just whitespace)
  //     const trimmedDescription = description.trim();
  //     
  //     const shareData = {
  //       game_name: game.name,
  //       map_configuration_json: game.cloudinary_url, // URL של מצב המשחק ב-Cloudinary
  //       description: trimmedDescription
  //     };
  //     
  //     const result = await shareGame(shareData);
  //     
  //     if (result) {
  //       alert('Game shared successfully!');
  //     } else {
  //       alert('Failed to share game. Please try again.');
  //     }
  //   }
  // };

  const handleTogglePrivacy = async (gameId) => {
    try {
      // מצא את המשחק הנוכחי
      const currentGame = games.find(game => game.game_state_id === gameId);
      if (!currentGame) {
        setStatusMessage('Game not found');
        setTimeout(() => setStatusMessage(''), 3000);
        return;
      }

      const newIsPublic = !currentGame.is_public;
      
      const response = await fetch(`/api/game-states/${gameId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ isPublic: newIsPublic })
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.statusText}`);
      }

      // רענון רשימת המשחקים
      await fetchGames();

      setStatusMessage(`Game ${newIsPublic ? 'made public' : 'made private'} successfully!`);
      setTimeout(() => setStatusMessage(''), 3000);

    } catch (error) {
      console.error('Error toggling privacy:', error);
      setStatusMessage(`Error updating privacy: ${error.message}`);
      setTimeout(() => setStatusMessage(''), 5000);
    }
  };

  const handleChangeImage = async (gameId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.style.display = 'none';
    
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      // Start loading state
      setUploadingImages(prev => new Set([...prev, gameId]));
      
      try {
        // Show compression message for large files
        const originalSizeMB = (file.size / (1024 * 1024)).toFixed(2);
        
        let processedFile = file;
        
        // Resize large images to optimize for thumbnails while preserving quality
        if (file.size > 1 * 1024 * 1024) { // If larger than 1MB
          setStatusMessage('Resizing image for thumbnail...');
          
          processedFile = await compressThumbnailImage(file, {
            maxWidth: 1200,
            maxHeight: 1200,
            quality: 0.85,
            outputFormat: 'jpeg'
          });
          
          const compressedSizeMB = (processedFile.size / (1024 * 1024)).toFixed(2);
          setStatusMessage(`Image resized: ${originalSizeMB} MB → ${compressedSizeMB} MB`);
        }
        
        const formData = new FormData();
        formData.append('file', processedFile);
        
        const response = await fetch(`/api/game-states/${gameId}/upload-image`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: formData
        });
        
        if (!response.ok) {
          throw new Error(`Upload failed: ${response.statusText}`);
        }
        
        const result = await response.json();
        
        if (result.success) {
          // Refresh games to get updated thumbnail
          fetchGames();
          setStatusMessage('Thumbnail uploaded successfully!');
          setTimeout(() => setStatusMessage(''), 3000);
        } else {
          throw new Error(result.message || 'Upload failed');
        }
        
      } catch (error) {
        console.error('Error uploading thumbnail:', error);
        
        // Handle specific error types
        let errorMessage = 'Failed to upload image. Please try again.';
        if (error.message.includes('File too large')) {
          errorMessage = 'Image too large! Please use an image smaller than 10MB.';
        } else if (error.message.includes('Only image files')) {
          errorMessage = 'Please select an image file (JPG, PNG, etc.).';
        } else if (error.message.includes('quota')) {
          errorMessage = 'Storage limit reached. Please contact support.';
        }
        
        setStatusMessage(errorMessage);
        setTimeout(() => setStatusMessage(''), 7000);
      } finally {
        // End loading state
        setUploadingImages(prev => {
          const newSet = new Set(prev);
          newSet.delete(gameId);
          return newSet;
        });
      }
    };
    
    document.body.appendChild(input);
    input.click();
    document.body.removeChild(input);
  };

  const handleToggleImageExpand = (gameId) => {
    setExpandedImages(prev => {
      const newSet = new Set(prev);
      const placeholderElement = document.querySelector(
        `[data-game-id="${gameId}"] .game-image-placeholder`
      );
      
      if (newSet.has(gameId)) {
        // Collapsing - reset height to auto
        newSet.delete(gameId);
        if (placeholderElement) {
          placeholderElement.style.height = '';
        }
      } else {
        // Expanding - calculate optimal height
        newSet.add(gameId);
        
        setTimeout(() => {
          const game = games.find(g => g.game_state_id === gameId);
          if (game && game.thumbnail_url && placeholderElement) {
            const imageElement = new Image();
            imageElement.onload = () => {
              const aspectRatio = imageElement.naturalHeight / imageElement.naturalWidth;
              const calculatedHeight = 500 * aspectRatio;
              
              // Set calculated height for the expanded image
              placeholderElement.style.height = `${calculatedHeight}px`;
            };
            imageElement.src = game.thumbnail_url;
          }
        }, 100); // Small delay to ensure DOM update and CSS transition
      }
      return newSet;
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  const handleToggleHistoryDebug = (checked) => {
    try {
      localStorage.setItem('dnd_show_history_debug_panel', checked ? 'true' : 'false');
    } catch (_) {}
    setHistoryDebugVisible(checked);
  };

  // Function to resize images for thumbnails while preserving aspect ratio
  const compressThumbnailImage = async (file, options = {}) => {
    const {
      maxWidth = 1600,
      maxHeight = 1600,
      quality = 1.0,
      outputFormat = 'jpeg'
    } = options;

    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        try {
          // Calculate new dimensions while maintaining aspect ratio
          let { width, height } = img;

          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }

          // Set canvas dimensions
          canvas.width = width;
          canvas.height = height;

          // Draw and compress
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                // Create a File object from the blob
                const compressedFile = new File([blob], file.name, {
                  type: `image/${outputFormat}`,
                  lastModified: Date.now()
                });
                resolve(compressedFile);
              } else {
                reject(new Error('Failed to compress image'));
              }
            },
            `image/${outputFormat}`,
            quality
          );
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => reject(new Error('Failed to load image'));

      // Convert file to data URL
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const getGameMetadata = (game) => {
    try {
      return typeof game.metadata === 'string' 
        ? JSON.parse(game.metadata) 
        : game.metadata || {};
    } catch (e) {
      return {};
    }
  };

  // פונקציה לעריכת שם המשחק
  const handleGameNameEdit = async (gameId, newName) => {
    if (!newName || newName.trim() === '') {
      setStatusMessage('Game name cannot be empty');
      setTimeout(() => setStatusMessage(''), 3000);
      return;
    }

    const trimmedName = newName.trim();
    
    // בדיקה אם השם השתנה
    const currentGame = games.find(game => game.game_state_id === gameId);
    if (!currentGame || currentGame.name === trimmedName) {
      // השם לא השתנה - אין צורך לשמור
      return;
    }
    
    try {
      const response = await fetch(`/api/game-states/${gameId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ name: trimmedName })
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.statusText}`);
      }

      // רענון רשימת המשחקים
      await fetchGames();

      setStatusMessage('Game name updated successfully!');
      setTimeout(() => setStatusMessage(''), 3000);

    } catch (error) {
      console.error('Error updating game name:', error);
      setStatusMessage(`Error updating game name: ${error.message}`);
      setTimeout(() => setStatusMessage(''), 5000);
    }
  };

  // פונקציה לעריכת תיאור המשחק
  const handleGameDescriptionEdit = async (gameId, newDescription) => {
    const trimmedDescription = newDescription.trim();
    
    // בדיקה אם התיאור השתנה
    const currentGame = games.find(game => game.game_state_id === gameId);
    if (!currentGame || currentGame.description === trimmedDescription) {
      // התיאור לא השתנה - אין צורך לשמור
      return;
    }
    
    try {
      const response = await fetch(`/api/game-states/${gameId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ description: trimmedDescription })
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.statusText}`);
      }

      // רענון רשימת המשחקים
      await fetchGames();

      setStatusMessage('Game description updated successfully!');
      setTimeout(() => setStatusMessage(''), 3000);

    } catch (error) {
      console.error('Error updating game description:', error);
      setStatusMessage(`Error updating game description: ${error.message}`);
      setTimeout(() => setStatusMessage(''), 5000);
    }
  };

  // פונקציה להתחלת עריכת תיאור
  const handleStartEditingDescription = (gameId) => {
    setEditingDescriptions(prev => new Set([...prev, gameId]));
  };

  // פונקציה לביטול עריכת תיאור
  const handleCancelEditingDescription = (gameId) => {
    setEditingDescriptions(prev => {
      const newSet = new Set(prev);
      newSet.delete(gameId);
      return newSet;
    });
  };

  // פונקציה לשמירת תיאור
  const handleSaveDescription = async (gameId, description) => {
    await handleGameDescriptionEdit(gameId, description);
    handleCancelEditingDescription(gameId);
  };



  if (!currentUser) {
    return (
      <div className="profile-page">
        <div className="profile-container">
          <h1>Please log in to view your profile</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-container">
        <div className="profile-header">
          <h1><i className="fas fa-user"></i> Profile</h1>
          <div className="user-info">
            <h2>Welcome, {currentUser.username}!</h2>
          </div>
        </div>

        <div className="profile-content">
          {/* Games Section */}
          <div className="games-section">
            <div className="section-header">
              <h3><i className="fas fa-gamepad"></i> Your Saved Games ({games.length})</h3>
              <button 
                className="toggle-descriptions-btn" 
                onClick={() => setShowAllDescriptions(!showAllDescriptions)}
                title={showAllDescriptions ? "Hide all descriptions" : "Show all descriptions"}
                style={{
                  background: 'var(--primary-color)',
                  color: 'var(--text-color)',
                  border: '2px solid var(--primary-color)',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-primary)',
                  fontWeight: '600',
                  fontSize: 'var(--font-size-sm)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'var(--secondary-color)';
                  e.target.style.borderColor = 'var(--secondary-color)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'var(--primary-color)';
                  e.target.style.borderColor = 'var(--primary-color)';
                }}
              >
                <i className={`fas ${showAllDescriptions ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                {showAllDescriptions ? 'Hide Descriptions' : 'Show Descriptions'}
              </button>
          </div>
          
            {loading && <div className="loading">Loading games...</div>}
            {error && <div className="error">Error: {error}</div>}

            {games.length === 0 && !loading ? (
              <div className="no-games">
                <p>No saved games yet.</p>
                <p>Create your first game in the map editor!</p>
              </div>
            ) : (
              <div className="games-grid">
                {games.map((game) => {
                  const metadata = getGameMetadata(game);
                  return (
                    <div key={game.game_state_id} className="game-card" data-game-id={game.game_state_id}>
                      <div className="game-content">
                        <div className="game-info-column">
                          <div className="game-top-row">
                            <h4 className="game-title">
                              <span 
                                className="game-name-text"
                                contentEditable={false}
                                suppressContentEditableWarning={true}
                                title="Click to select game, double-click to edit name"
                                onBlur={(e) => {
                                  e.target.contentEditable = false;
                                  handleGameNameEdit(game.game_state_id, e.target.textContent);
                                }}
                                onDoubleClick={(e) => {
                                  e.stopPropagation();
                                  // Double click - enable editing
                                  e.target.contentEditable = true;
                                  e.target.focus();
                                  // Select all text for easy editing
                                  const range = document.createRange();
                                  range.selectNodeContents(e.target);
                                  const selection = window.getSelection();
                                  selection.removeAllRanges();
                                  selection.addRange(range);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.target.blur();
                                  }
                                  if (e.key === 'Escape') {
                                    e.target.textContent = game.name;
                                    e.target.blur();
                                  }
                                }}
                              >
                                {game.name}
                              </span>
                            </h4>
                            <div className="game-actions">
                              <button 
                                onClick={() => handleLoadGame(game.game_state_id)}
                                className="load-btn"
                                title="Load Game"
                              >
                                <i className="fas fa-folder-open"></i>
                              </button>
                              <button 
                                onClick={() => handleChangeImage(game.game_state_id)}
                                className="change-image-btn"
                                title={uploadingImages.has(game.game_state_id) ? "Uploading image..." : "Change Game Image"}
                                disabled={uploadingImages.has(game.game_state_id)}
                              >
                                {uploadingImages.has(game.game_state_id) ? (
                                  <i className="fas fa-spinner fa-spin"></i>
                                ) : (
                                  <i className="fas fa-image"></i>
                                )}
                              </button>
                              <button 
                                onClick={() => handleStartEditingDescription(game.game_state_id)}
                                className="edit-description-btn"
                                title="Edit Game Description"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button 
                                onClick={() => handleDeleteGame(game.game_state_id)}
                                className="delete-btn"
                                title={deletingGames.has(game.game_state_id) ? "Deleting game..." : "Delete Game"}
                                disabled={deletingGames.has(game.game_state_id)}
                              >
                                {deletingGames.has(game.game_state_id) ? (
                                  <i className="fas fa-spinner fa-spin"></i>
                                ) : (
                                  <i className="fas fa-trash"></i>
                                )}
                              </button>
                            </div>
                          </div>
                          
                          {/* Game Middle Row - Description Edit Area */}
                          {(showAllDescriptions || editingDescriptions.has(game.game_state_id)) && (
                            <div className="game-middle-row">
                              {editingDescriptions.has(game.game_state_id) ? (
                                <div className="description-edit-area">
                                  <div style={{
                                    display: 'flex',
                                    gap: '8px',
                                    alignItems: 'flex-start'
                                  }}>
                                    <textarea
                                      className="description-edit-input"
                                      defaultValue={game.description || ''}
                                      placeholder="Enter a description for this game..."
                                      rows={3}
                                      style={{
                                        flex: '1',
                                        padding: '10px',
                                        border: '2px solid var(--border-color)',
                                        borderRadius: '6px',
                                        background: 'var(--background-color)',
                                        color: 'var(--text-color)',
                                        fontFamily: 'var(--font-secondary)',
                                        fontSize: 'var(--font-size-base)',
                                        resize: 'vertical',
                                        minHeight: '60px'
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' && e.ctrlKey) {
                                          handleSaveDescription(game.game_state_id, e.target.value);
                                        }
                                        if (e.key === 'Escape') {
                                          handleCancelEditingDescription(game.game_state_id);
                                        }
                                      }}
                                    />
                                    <div className="description-edit-actions" style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '4px',
                                      alignSelf: 'center'
                                    }}>
                                      <button 
                                        className="save-description-btn"
                                        onClick={(e) => {
                                          const textarea = e.target.closest('.description-edit-area').querySelector('textarea');
                                          handleSaveDescription(game.game_state_id, textarea.value);
                                        }}
                                        title="Save description (Ctrl+Enter)"
                                        style={{
                                          background: 'var(--primary-color)',
                                          color: 'var(--text-color)',
                                          border: '2px solid var(--primary-color)',
                                          padding: '8px',
                                          borderRadius: '4px',
                                          cursor: 'pointer',
                                          fontSize: 'var(--font-size-sm)',
                                          fontFamily: 'var(--font-primary)',
                                          fontWeight: '600',
                                          width: '36px',
                                          height: '36px',
                                          minHeight: '36px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center'
                                        }}
                                      >
                                        <i className="fas fa-save"></i>
                                      </button>
                                      <button 
                                        className="cancel-description-btn"
                                        onClick={() => handleCancelEditingDescription(game.game_state_id)}
                                        title="Cancel editing (Escape)"
                                        style={{
                                          background: 'transparent',
                                          color: 'var(--text-muted)',
                                          border: '2px solid var(--text-muted)',
                                          padding: '8px',
                                          borderRadius: '4px',
                                          cursor: 'pointer',
                                          fontSize: 'var(--font-size-sm)',
                                          fontFamily: 'var(--font-primary)',
                                          fontWeight: '600',
                                          width: '36px',
                                          height: '36px',
                                          minHeight: '36px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center'
                                        }}
                                      >
                                        <i className="fas fa-times"></i>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="description-display-area">
                                  {game.description ? (
                                    <p className="game-description" style={{
                                      margin: '0',
                                      padding: '10px',
                                      background: 'var(--background-color)',
                                      border: '1px solid var(--border-color)',
                                      borderRadius: '6px',
                                      color: 'var(--text-color)',
                                      fontFamily: 'var(--font-secondary)',
                                      fontSize: 'var(--font-size-base)',
                                      lineHeight: '1.5'
                                    }}>
                                      {game.description}
                                    </p>
                                  ) : (
                                    <p className="no-description" style={{
                                      margin: '0',
                                      padding: '10px',
                                      background: 'var(--surface-color)',
                                      border: '1px solid var(--border-color)',
                                      borderRadius: '6px',
                                      color: 'var(--text-muted)',
                                      fontFamily: 'var(--font-secondary)',
                                      fontSize: 'var(--font-size-base)',
                                      fontStyle: 'italic'
                                    }}>
                                      No description provided
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                          
                          <div className="game-bottom-row">
                            {!showAllDescriptions && game.description && (
                              <p className="game-description">{game.description}</p>
                            )}
                            
                            <div className="game-details">
                              <div className="info-row" title="Created Date">
                                <svg className="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                  <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
                                  <rect x="2" y="2" width="20" height="20" rx="2" ry="2" fill="none" stroke="currentColor"/>
                                </svg>
                                <span className="value">{formatDate(game.created_at)}</span>
                              </div>
                              <div className="info-row" title="Last Updated">
                                <svg className="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                  <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/>
                                  <path d="M21 3v5h-5"/>
                                </svg>
                                <span className="value">{formatDate(game.updated_at)}</span>
                              </div>
                              <div className="info-row" title="Privacy Settings">
                                <div className="privacy-controls">
                                  <button 
                                    className={`privacy-status-btn ${game.is_public ? 'public' : 'private'}`}
                                    onClick={() => handleTogglePrivacy(game.game_state_id)}
                                  >
                                    {game.is_public ? 'PUBLIC' : 'PRIVATE'}
                                  </button>
                                  <span title="Toggle between Public/Private">
                                    {game.is_public ? (
                                      <svg className="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                        <circle cx="12" cy="12" r="10"/>
                                        <path d="M2 12h20"/>
                                        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                                      </svg>
                                    ) : (
                                      <svg className="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                                        <circle cx="12" cy="16" r="1"/>
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                                      </svg>
                                    )}
                                  </span>
                                </div>
                              </div>
                              {metadata.layerCount !== undefined && (
                                <div className="info-row" title="Number of Layers">
                                  <svg className="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                    <rect x="7" y="7" width="10" height="10" rx="1" ry="1"/>
                                    <rect x="10" y="10" width="4" height="4" rx="1" ry="1"/>
                                  </svg>
                                  <span className="value">{metadata.layerCount}</span>
                                </div>
                              )}
                              {metadata.containerCount !== undefined && (
                                <div className="info-row" title="Number of Containers">
                                  <svg className="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                    <path d="M9 9h6v6H9z"/>
                                    <path d="M12 3v18"/>
                                    <path d="M3 12h18"/>
                                  </svg>
                                  <span className="value">{metadata.containerCount}</span>
                                </div>
                              )}
                              {metadata.estimatedSize && (
                                <div className="info-row" title="File Size">
                                  <svg className="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                    <polyline points="14,2 14,8 20,8"/>
                                    <line x1="16" y1="13" x2="8" y2="13"/>
                                    <line x1="16" y1="17" x2="8" y2="17"/>
                                    <polyline points="10,9 9,9 8,9"/>
                                  </svg>
                                  <span className="value">{formatFileSize(metadata.estimatedSize)}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div 
                          className={`game-image-column ${expandedImages.has(game.game_state_id) ? 'expanded' : ''}`}
                          onClick={() => handleToggleImageExpand(game.game_state_id)}
                        >
                          <div 
                            className="game-image-placeholder"
                            style={game.thumbnail_url && game.thumbnail_url.includes('http') ? { backgroundImage: `url(${game.thumbnail_url})` } : {}}
                          >
                                                          {(!game.thumbnail_url || !game.thumbnail_url.includes('http')) && (
                              <svg className="placeholder-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                <circle cx="8.5" cy="8.5" r="1.5"/>
                                <polyline points="21,15 16,10 5,21"/>
                              </svg>
                            )}
                            {game.thumbnail_url && (
                              <div className="image-overlay">
                                <span>Click to {expandedImages.has(game.game_state_id) ? 'collapse' : 'expand'}</span>
                              </div>
                            )}
                            {!expandedImages.has(game.game_state_id) && (
                              <div className="expand-hint">
                                <i className="fas fa-expand"></i>
                              </div>
                            )}
                            {expandedImages.has(game.game_state_id) && (
                              <div className="collapse-hint">
                                <i className="fas fa-times"></i>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {currentUser?.isAdmin && (
            <div className="admin-section" style={{ marginTop: '24px' }}>
              <div className="section-header">
                <h3><i className="fas fa-tools"></i> Admin Controls</h3>
              </div>
              <div className="admin-controls" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                background: 'var(--surface-color)'
              }}>
                <label htmlFor="toggle-history-debug" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    id="toggle-history-debug"
                    type="checkbox"
                    checked={historyDebugVisible}
                    onChange={(e) => handleToggleHistoryDebug(e.target.checked)}
                  />
                  <span>Show History Debug Panel</span>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Status Message */}
        {statusMessage && (
          <div className="status-message">
            {statusMessage}
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && (
          <div className="confirmation-overlay">
            <div className="confirmation-dialog">
              <div className="confirmation-header">
                <h3>Delete Game</h3>
                <button 
                  className="close-btn"
                  onClick={handleCancelDeleteGame}
                >
                  ×
                </button>
              </div>
              <div className="confirmation-body">
                <p>Are you sure you want to delete the game "{gameToDelete?.gameName}"?</p>
                <p className="warning-text">
                  <i className="fas fa-exclamation-triangle"></i>
                  This action cannot be undone.
                </p>
              </div>
              <div className="confirmation-actions">
                <button 
                  className="confirm-btn cancel-btn"
                  onClick={handleCancelDeleteGame}
                >
                  Cancel
                </button>
                <button 
                  className="confirm-btn delete-btn"
                  onClick={handleConfirmDeleteGame}
                >
                  Delete Game
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage; 